<template>
  <section class="py-10 bg-white">
    <div class="max-w-6xl mx-auto px-6">
      <!-- 헤더 -->
      <div class="mb-10">
        <h2 class="text-[32px] font-extrabold text-slate-900 tracking-tight mb-2">지금 핫한 분양 현장</h2>
        <p class="text-[16px] text-slate-500 font-medium">실시간 인기 현장을 확인해보세요</p>
      </div>

      <!-- 슬라이더 -->
      <div class="relative">
        <!-- 네비게이션 버튼 -->
        <button
          v-show="canScrollLeft"
          @click="scroll('left')"
          class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white border border-slate-200 rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors"
        >
          <svg class="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>

        <button
          v-show="canScrollRight"
          @click="scroll('right')"
          class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white border border-slate-200 rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors"
        >
          <svg class="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>

        <!-- 스크롤 영역 -->
        <div
          ref="scrollContainer"
          @scroll="checkScroll"
          class="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
        >
          <!-- 로딩 -->
          <div v-if="isLoading" class="flex-1 py-12 text-center text-slate-400 text-[14px]">
            로딩중...
          </div>

          <!-- 빈 상태 -->
          <div v-else-if="!hotAd && hotProperties.length === 0" class="flex-1 py-12 text-center text-slate-400 text-[14px]">
            등록된 핫 현장이 없습니다.
          </div>

          <template v-else>
            <!-- AD 카드 -->
            <div
              v-if="hotAd"
              @click="handleAdClick"
              class="flex-shrink-0 w-[280px] flex items-center gap-4 p-4 bg-orange-50 border border-orange-200 rounded-2xl cursor-pointer hover:border-orange-300 hover:shadow-md transition-all"
            >
              <div class="w-11 h-11 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <span class="text-white text-[11px] font-bold">AD</span>
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="text-[15px] font-bold text-slate-900 truncate mb-0.5">{{ hotAd.propertyName }}</h3>
                <p class="text-[13px] text-slate-500 truncate">{{ hotAd.address }}</p>
              </div>
            </div>

            <!-- 일반 카드 -->
            <div
              v-for="(property, index) in hotProperties"
              :key="property.id"
              @click="goToProperty(property.id)"
              class="flex-shrink-0 w-[280px] flex items-center gap-4 p-4 bg-white shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-all"
            >
              <div class="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <span class="text-[15px] font-bold text-blue-600">{{ index + 1 }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="text-[15px] font-bold text-slate-900 truncate mb-0.5">{{ property.name }}</h3>
                <p class="text-[13px] text-slate-500 truncate">{{ property.address }}</p>
              </div>
              <div class="flex items-center gap-1 flex-shrink-0">
                <svg class="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                <span class="text-[12px] font-semibold text-blue-600">{{ property.likeCount || 0 }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- 하단 버튼 -->
      <div class="text-center mt-8">
        <router-link
          to="/properties"
          class="inline-flex items-center gap-1 text-[15px] font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-5 py-2.5 rounded-xl transition-all"
        >
          분양 일정 전체보기
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useHotProperty } from '@/composables/main/useHotProperty'

export default {
  name: 'HotSection',
  setup() {
    const router = useRouter()
    const { getHotProperties, getHotAd } = useHotProperty()

    const scrollContainer = ref(null)
    const canScrollLeft = ref(false)
    const canScrollRight = ref(true)
    const hotProperties = ref([])
    const hotAd = ref(null)
    const isLoading = ref(true)

    const scroll = (direction) => {
      const container = scrollContainer.value
      if (!container) return
      const scrollAmount = 300
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }

    const checkScroll = () => {
      const container = scrollContainer.value
      if (!container) return
      canScrollLeft.value = container.scrollLeft > 0
      canScrollRight.value = Math.ceil(container.scrollLeft + container.clientWidth) < container.scrollWidth
    }

    const goToProperty = (propertyId) => {
      router.push(`/properties/${propertyId}`)
    }

    const handleAdClick = () => {
      if (hotAd.value?.campaignCode) {
        router.push(`/p/${hotAd.value.campaignCode}`)
      } else if (hotAd.value?.propertyId) {
        router.push(`/property/${hotAd.value.propertyId}`)
      }
    }

    const fetchData = async () => {
      try {
        isLoading.value = true
        const [ad, properties] = await Promise.all([
          getHotAd(),
          getHotProperties(5)
        ])
        hotAd.value = ad
        hotProperties.value = properties
      } catch (error) {
        console.error('Hot Section 로딩 실패:', error)
      } finally {
        isLoading.value = false
      }
    }

    onMounted(async () => {
      await fetchData()
      setTimeout(() => {
        checkScroll()
        window.addEventListener('resize', checkScroll)
      }, 100)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', checkScroll)
    })

    return {
      scrollContainer,
      canScrollLeft,
      canScrollRight,
      hotProperties,
      hotAd,
      isLoading,
      scroll,
      checkScroll,
      goToProperty,
      handleAdClick
    }
  }
}
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
